import classNames from 'classnames';
import { memo, useMemo } from 'react';
import {
  GameData,
  MESSAGE_STATUS,
  Winner,
  getCurrencyIcon,
  makeFloat,
  useApiContext,
  useMessageContext
} from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useGameAvailabilityCheck } from 'src/components/GameCard/hooks/useGameCheck';
import { GameImage } from 'src/components/GameImage';
import { useAuthFlow } from 'src/modules/authFlow/hooks/useAuthFlow';
import { useRoute } from 'src/router/hooks';

interface RecentWinnerItemProps {
  winner: Winner;
  handlePlayMarquee: () => void;
  handleStopMarquee: () => void;
}

const RecentWinnerItem = ({
  winner: { game, login, currency, amount },
  handlePlayMarquee,
  handleStopMarquee
}: RecentWinnerItemProps) => {
  const navigate = useNavigate();
  const { ROUTE } = useRoute();
  const { t } = useTranslation();
  const { isAuthenticated } = useApiContext();
  const { name, providerName, geo_link, id } = game;
  const { startRegistration } = useAuthFlow();
  const gameCheck = useGameAvailabilityCheck();
  const { openMessage } = useMessageContext();

  const handleGameNavigate = () => {
    const doNavigate = () => geo_link && navigate(ROUTE.GAME.get(String(id), ''));
    if (!isAuthenticated) {
      startRegistration({ onSuccess: doNavigate });
      return;
    }
    if (!gameCheck(id)) {
      openMessage?.(t('gameNotification.notAvailable'), MESSAGE_STATUS.ERROR);
      return;
    }
    doNavigate();
  };

  const gameInfoText = useMemo(() => {
    const provider = providerName ? `${t('general.by')} ${providerName}` : '';

    return (
      <div className="font-bold text-xs text-mp_primary_text_recent_winners first-letter:uppercase">
        <p className="text-mp_secondary_text_recent_winners">
          {t('general.in')} &quot;{name}&quot;
        </p>
        <p>{provider}</p>
      </div>
    );
  }, [providerName, name]);

  return (
    <div
      onClick={handleGameNavigate}
      onMouseEnter={handlePlayMarquee}
      onMouseLeave={handleStopMarquee}
      className={classNames(
        'w-[250px] flex items-center overflow-hidden h-[110px] rounded-[5px] bg-mp_recent_winners_bg mx-2',
        geo_link ? 'cursor-pointer' : 'cursor-default'
      )}>
      {game ? (
        <GameImage
          game={game as unknown as GameData}
          className="min-w-[86px] w-[86px] shadow-game object-fill"
        />
      ) : null}

      <div className="w-full p-2 text-mp_primary_text_recent_winners">
        <p className="font-medium text-sm">{login}</p>
        <p className="font-bold text-xl">
          {getCurrencyIcon(currency)} {makeFloat(amount)}
        </p>
        {gameInfoText}
      </div>
    </div>
  );
};

export default memo(RecentWinnerItem);
